import { FeaturesReady } from '@growthbook/growthbook-react';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import {
  EXPERIMENT_CARD_BADGE_HEIGHT,
  EXPERIMENT_CARD_BADGE_WIDTH
} from 'dpl/breeder_preferred_qualifications/utils/constants';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { PhotographPropTypes } from 'dpl/shared/utils/customPropTypes';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { isNumeric } from 'dpl/shared/form/utils';
import Icon from 'dpl/common/components/Icon';
import { TRANSPORT_TYPES } from 'dpl/breeder_search_results/utils/constants';
import {
  LITTER_ID,
  PUPPY_ID,
  FILTER_LOCATION_NAME
} from 'dpl/constants/query_params';
import CertifiedBreederBadge from 'dpl/experiments/exp_breeder_search_updates/breeder_profile/components/CertifiedBreederBadge';
import CardMediaCarouselV2 from 'dpl/breeder_search_results/components/CardMediaCarouselV2';
import TransportationModal from 'dpl/modals/TransportationModal';
import { addToQuery } from 'dpl/util/queryString';
import { removeEmptyValues } from 'dpl/shared/utils/object';
import { TAB_KEYS_EXPERIMENT } from 'dpl/components/BreederProfile/utils/constants';

const CARD_PHOTO_SIZE = isLessThanBreakpoint('lg') ? '212px' : '260px';
const NEARBY_MAX_DISTANCE = 150;
const { AIR, GROUND } = TRANSPORT_TYPES;

function Transport({
  deliveryMethods,
  breederFirstName,
  facilityLocation,
  pickupOptions,
  distance
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const transportTypes = Object.keys(deliveryMethods);

  const hasTransportation =
    transportTypes.includes(AIR) || transportTypes.includes(GROUND);

  function handleOpenClick(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  }

  function handleCloseClick(e) {
    e?.stopPropagation?.();
    setIsModalOpen(false);
  }

  const mileOrMiles = distance === 1 ? 'mile' : 'miles';

  const milesAway = isNumeric(distance)
    ? `${distance} ${mileOrMiles} away`
    : '';

  const isNearby = distance <= NEARBY_MAX_DISTANCE;

  return (
    <div className="BreederSearchResultsTransport font-12 md:font-14 secondary truncate">
      {isNearby && milesAway ? (
        <p>{milesAway}</p>
      ) : (
        <button
          type="button"
          className="flex items-center pointer underline"
          tabIndex="0"
          onClick={handleOpenClick}
        >
          {hasTransportation ? 'Drop off to you' : 'Meet for pickup'}
        </button>
      )}
      {isModalOpen && (
        <TransportationModal
          onCloseClick={handleCloseClick}
          deliveryMethods={deliveryMethods}
          breederFirstName={breederFirstName}
          facilityLocation={facilityLocation}
          pickupOptions={pickupOptions}
          distance={distance}
        />
      )}
    </div>
  );
}

Transport.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  deliveryMethods: PropTypes.object.isRequired,
  breederFirstName: PropTypes.string,
  facilityLocation: PropTypes.string,
  distance: PropTypes.number,
  pickupOptions: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      distance: PropTypes.number,
      key: PropTypes.string
    })
  )
};

Transport.defaultProps = {
  breederFirstName: null,
  facilityLocation: null,
  distance: null,
  pickupOptions: []
};

const SMART_IMAGE_PROPS = {
  Tag: 'div',
  className: 'bg-center cover br3',
  crop: true,
  loadingClass: 'bg-light-gray',
  height: CARD_PHOTO_SIZE,
  width: CARD_PHOTO_SIZE,
  allowContextMenu: true
};

function getBreederProfileUrl(url, params = {}) {
  const separator = url.includes('#') ? '&' : '#';
  const query = addToQuery(removeEmptyValues(params));

  return [url, query].join(separator);
}

export default function Card({
  profile,
  lastUpdated,
  analytics,
  onFavoriteChange,
  trackInView
}) {
  const locationTitle = useSelector(({ server }) => server.locationTitle);

  const locName = useSelector(
    ({ queryParams }) => queryParams[FILTER_LOCATION_NAME]
  );

  const {
    cropped_photo_url: croppedPhotoUrl,
    delivery_methods: deliveryMethods,
    name,
    description,
    distance_in_miles: distance,
    availability_description: availabilityDescription,
    available_puppy_count: availablePuppyCount,
    profile_photo_url: profilePhotoUrl,
    snoozed,
    url,
    pickup_options: pickupOptions,
    first_name: breederFirstName,
    missing_photo_url: missingPhotoUrl,
    result_photos: resultPhotos,
    breeder_breed_id: breederBreedId,
    breed_id: breedId,
    id: breederProfileId,
    testimonials_count: testimonialsCount,
    preferred_qualification: preferredQualification
  } = profile;

  const photosWithAlt = useMemo(
    () =>
      resultPhotos.map(photo => {
        const clickthroughUrl = new URL(
          photo.clickthrough_url,
          window.location.origin
        );

        // Remove hash params if they exist
        if (clickthroughUrl.hash) {
          const hashParams = new URLSearchParams(clickthroughUrl.hash.slice(1));
          hashParams.delete(LITTER_ID);
          hashParams.delete(PUPPY_ID);

          // Only set the hash if there are remaining params
          clickthroughUrl.hash = hashParams.toString()
            ? `#${hashParams.toString()}`
            : '';
        }

        return {
          ...photo,
          alt:
            photo.alt_text ||
            (locationTitle ? `${name} in ${locationTitle}` : name),
          dog_modal_id: null,
          clickthrough_url: clickthroughUrl.toString()
        };
      }),
    [locationTitle, lastUpdated]
  );

  const displayPhotoUrl = croppedPhotoUrl || profilePhotoUrl;

  const breederProfileUrl = getBreederProfileUrl(url, { locName });

  const breederReviewsUrl = getBreederProfileUrl(url, {
    tab: TAB_KEYS_EXPERIMENT.STORIES,
    locName
  });

  const currentUser = useCurrentUser();
  const { isBuyer, isLoggedIn } = currentUser;

  const favoriteAnalytics = {
    ...analytics,
    breed_id: breedId,
    breeder_name: name,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    page_number: analytics.page_number || null,
    position: analytics.position || null,
    puppies_available: availablePuppyCount,
    transportation: Object.keys(deliveryMethods || {}).reduce(
      (allTypes, key) => {
        return [...allTypes, ...deliveryMethods[key].map(dm => dm.name)];
      },
      []
    )
  };

  const shouldShowFavoriteButton = !isLoggedIn || isBuyer;

  const computedAnalytics = {
    ...analytics,
    breeder_preferred_status: preferredQualification?.status,
    breeder_profile_id: breederProfileId,
    view_feature: ANALYTICS_VIEW_FEATURES.BREEDER_CARD
  };

  function handleClick() {
    sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
      ...computedAnalytics,
      breed_id: breedId,
      miles_away: distance,
      transportation: favoriteAnalytics.transportation
    });
  }

  function handleReviewsClick(e) {
    e.preventDefault();
    e.stopPropagation();

    window.location.href = breederReviewsUrl;
  }

  const [ref] = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: inView => {
      if (inView) trackInView();
    }
  });

  return (
    <a
      ref={ref}
      className="BreederSearchResultsCard h-100 db"
      href={breederProfileUrl}
      onClick={handleClick}
    >
      <div className="BreederSearchResultsCard__container h-100 flex flex-column justify-between br12 ba b--default">
        <div>
          <div className="BreederSearchResultsCard__info flex ph4 pt4 mb2 justify-between gap-12 md:gap-16">
            <div className="flex items-start gap-8 md:gap-16 overflow-hidden">
              <div className="relative">
                <UserAvatar
                  user={{
                    id: profile.user_id,
                    initials: profile.initials,
                    profile_photo_url: displayPhotoUrl
                  }}
                  size="xxm"
                />
                <div className="absolute right-0 bottom-0 nb1">
                  <CertifiedBreederBadge
                    height={EXPERIMENT_CARD_BADGE_HEIGHT}
                    width={EXPERIMENT_CARD_BADGE_WIDTH}
                  />
                </div>
              </div>
              <div className="overflow-auto">
                <p className="BreederSearchResultsItem__name font-serif fw-medium lh-title font-18 md:font-20 truncate">
                  {name}
                </p>
                <div className="flex items-center gap-4 lh-1 overflow-hidden font-12 md:font-14 secondary">
                  <p className="truncate">Certified Breeder</p>
                  <span>·</span>
                  <Transport
                    deliveryMethods={deliveryMethods}
                    pickupOptions={pickupOptions}
                    breederFirstName={breederFirstName}
                    distance={distance}
                  />
                </div>
              </div>
            </div>
            {shouldShowFavoriteButton && (
              <div className="pv1 ph1">
                <FavoriteButton
                  analytics={favoriteAnalytics}
                  followableTypeId={breederBreedId}
                  followableType={FOLLOWABLE_TYPES.BREEDER_BREED}
                  onChange={onFavoriteChange}
                  checkedIconProps={{ name: 'fetch-heart-full-breeder' }}
                  uncheckedIconProps={{
                    name: 'fetch-heart',
                    className: 'secondary'
                  }}
                />
              </div>
            )}
          </div>
          {!snoozed && (
            <div className="flex mt4 mt6-md overflow-hidden">
              <div className="BreederSearchResultsItem__image w-100 h-100 br3 relative z-0">
                <FeaturesReady timeout={500}>
                  <CardMediaCarouselV2
                    mediaFiles={
                      photosWithAlt.length === 0
                        ? [{ url: missingPhotoUrl }]
                        : photosWithAlt
                    }
                    smartImageProps={SMART_IMAGE_PROPS}
                    showCaptions
                    analytics={{
                      ...computedAnalytics,
                      breeder_preferred_status: undefined,
                      breeder_profile_id: undefined
                    }}
                  />
                </FeaturesReady>
              </div>
            </div>
          )}
          <div className="mh4 mt4 pb4 f2">
            <p className="secondary">{description}</p>
          </div>
        </div>
        {!snoozed && (
          <>
            {(testimonialsCount > 0 || availabilityDescription) && (
              <div className="flex justify-between items-center font-14 ph4 pb4 pt3 bt b--default">
                {availabilityDescription && (
                  <p className="fw-medium">{availabilityDescription}</p>
                )}
                {testimonialsCount > 0 && (
                  <div className="flex items-center gap-4">
                    <Icon
                      className="secondary"
                      name="fetch-check"
                      height="20px"
                      width="20px"
                    />
                    <button
                      type="button"
                      className="secondary underline"
                      onClick={handleReviewsClick}
                    >
                      {testimonialsCount}
                      {testimonialsCount > 1 ? ' reviews' : ' review'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </a>
  );
}

Card.propTypes = {
  profile: PropTypes.shape({
    cropped_photo_url: PropTypes.string,
    availability_description: PropTypes.string,
    availability_info: PropTypes.string,
    facility_location_full_state: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    delivery_methods: PropTypes.object,
    description: PropTypes.string,
    snoozed: PropTypes.bool,
    name: PropTypes.string,
    initials: PropTypes.string.isRequired,
    facility_location: PropTypes.string,
    distance_in_miles: PropTypes.number,
    profile_photo_url: PropTypes.string,
    pickup_options: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string,
        title: PropTypes.string,
        subtitle: PropTypes.string,
        distance: PropTypes.number,
        key: PropTypes.string
      })
    ),
    result_photos: PropTypes.arrayOf(PropTypes.shape(PhotographPropTypes))
      .isRequired,
    url: PropTypes.string.isRequired,
    missing_photo_url: PropTypes.string,
    available_puppy_count: PropTypes.number,
    breeder_breed_id: PropTypes.number,
    id: PropTypes.number,
    user_id: PropTypes.number.isRequired,
    breed_id: PropTypes.number,
    testimonials_count: PropTypes.number,
    first_name: PropTypes.string,
    preferred_qualification: PropTypes.shape({
      status: PropTypes.string
    }),
    state_slug: PropTypes.string
  }).isRequired,
  analytics: PropTypes.shape({
    page_number: PropTypes.number,
    position: PropTypes.number,
    view: PropTypes.string
  }).isRequired,
  lastUpdated: PropTypes.number,
  onFavoriteChange: PropTypes.func,
  trackInView: PropTypes.func
};

Card.defaultProps = {
  lastUpdated: null,
  onFavoriteChange: null,
  trackInView: () => {}
};
